import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, AlertCircle, Clock, Coffee, Users, RotateCcw, BookOpen, ArrowLeft, CheckCircle, XCircle, Target, Zap, RefreshCw, ExternalLink } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "what-is-pomodoro", title: "What Pomodoro Actually Is" },
  { id: "what-pomodoro-is-not", title: "What Pomodoro Is NOT" },
  { id: "why-25-minutes", title: "Why 25 Minutes?" },
  { id: "breaks-are-fuel", title: "Breaks Are Fuel" },
  { id: "how-to-use-pomodoro", title: "How to Use Pomodoro" },
  { id: "pomodoro-and-study-streams", title: "Pomodoro + Study Streams" },
  { id: "if-pomodoro-didnt-work", title: "If Pomodoro Didn't Work Before" },
  { id: "final-thought", title: "Final Thought" },
  { id: "sources", title: "Sources" },
];

const Pomodoro101 = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Pomodoro Technique is a focus-and-break system. You work in short, timed intervals (typically 25 minutes), take intentional breaks (5 minutes), and repeat. After four cycles, you take a longer break. It's not about the timer—it's about protecting focus for a short, defined window."
        }
      },
      {
        "@type": "Question",
        "name": "Why is the Pomodoro Technique 25 minutes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "25 minutes isn't magic—it's psychologically safe. It's short enough that you can tell yourself 'I can do just 25 minutes,' which helps overcome the resistance to starting. Starting is the hardest part for most students, and a 25-minute session feels like a small commitment that often creates momentum."
        }
      },
      {
        "@type": "Question",
        "name": "Can I change the Pomodoro length?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. 25 minutes is not sacred. The point is the rhythm: focus → break → focus → break. You can adjust the timing once you understand the method. The structure matters more than the exact numbers."
        }
      },
      {
        "@type": "Question",
        "name": "Why are breaks important in the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Breaks are not a reward—breaks are fuel. Research supports the idea that brief, planned mental breaks can help prevent vigilance decline and keep performance steadier over time. If you're skipping breaks, Pomodoro will feel worse and stop working."
        }
      },
      {
        "@type": "Question",
        "name": "Does multitasking work with the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Research shows multitasking and frequent task-switching is linked to worse performance and weaker ability to filter distractions. Pomodoro works best when it's one task, one block, one focus target."
        }
      },
      {
        "@type": "Question",
        "name": "Why does Pomodoro work better with study streams?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pomodoro gets stronger when the rhythm is shared. Study streams and virtual study rooms add 'everyone is working right now' energy, synchronized start/stop times, and accountability without pressure. Shared work settings can improve persistence and reduce drifting."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Pomodoro 101: What the Pomodoro Technique Really Is (and What It Isn't)",
    "description": "A clear, research-backed guide to what the Pomodoro Technique actually is, common misconceptions, why 25 minutes works, and how to use it effectively for studying.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-26",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/pomodoro-101/"
    },
    "keywords": ["pomodoro technique", "pomodoro 101", "what is pomodoro", "focus technique", "study method", "time management", "productivity", "study tips", "study stream", "online workspace", "virtual study room", "collaborative study", "focus timer", "25 minute timer", "study with me", "productivity technique", "online study", "study session", "focus method", "concentration technique", "Francesco Cirillo"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pomodoro 101: What the Pomodoro Technique Really Is | CSW",
    "description": "A clear, research-backed guide to what the Pomodoro Technique actually is, common misconceptions, why 25 minutes works, and how to use it effectively for studying.",
    "url": "https://csw.live/pomodoro-technique/pomodoro-101/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Pomodoro Technique"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Productivity Method" },
      { "@type": "Thing", "name": "Time Management" },
      { "@type": "Thing", "name": "Study With Me" },
      { "@type": "Thing", "name": "Collaborative Study" },
      { "@type": "Thing", "name": "Online Study Platform" },
      { "@type": "Thing", "name": "Francesco Cirillo" },
      { "@type": "Thing", "name": "25 Minute Timer" }
    ],
    "keywords": "pomodoro technique, pomodoro 101, what is pomodoro, focus technique, study method, time management, productivity, study tips, study stream, online workspace, virtual study room, collaborative study, focus timer, study with me, online study, productivity technique, concentration, focus method"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Pomodoro 101",
        "item": "https://csw.live/pomodoro-technique/pomodoro-101/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "The Science of Focus",
      path: "/pomodoro-technique/science-of-focus/",
      description: "Why 25 minutes is enough (most of the time)"
    },
    {
      title: "Pomodoro vs. Procrastination",
      path: "/pomodoro-technique/pomodoro-vs-procrastination/",
      description: "How short sessions make starting easier"
    },
    {
      title: "Building Better Study Habits",
      path: "/pomodoro-technique/study-habits/",
      description: "How Pomodoro creates consistency that lasts"
    },
    {
      title: "Customize Your Pomodoro",
      path: "/pomodoro-technique/customize/",
      description: "Find the timing that works for you"
    },
    {
      title: "Why Breaks Matter",
      path: "/pomodoro-technique/why-breaks-matter/",
      description: "The science behind rest and recovery"
    },
    {
      title: "Pomodoro for ADHD",
      path: "/pomodoro-technique/adhd/",
      description: "How timed focus helps ADHD brains"
    },
    {
      title: "Avoid Burnout with Pomodoro",
      path: "/pomodoro-technique/avoid-burnout/",
      description: "Sustainable studying that protects your energy"
    },
    {
      title: "Best Pomodoro Tools",
      path: "/pomodoro-technique/best-tools/",
      description: "Apps and timers that actually work"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Pomodoro 101: What the Pomodoro Technique Really Is | CSW"
        description="A clear, research-backed guide to what the Pomodoro Technique actually is, common misconceptions, why 25 minutes works, and how to use it effectively for studying."
        canonicalUrl="/pomodoro-technique/pomodoro-101/"
      />
      <Header />
      <BlogReadingProgress />
      
      {/* Hero Section */}
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Pomodoro 101</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <Timer className="w-4 h-4" />
                <span>Fundamentals</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Pomodoro 101: What the Technique{" "}
                <span className="text-primary">Really Is</span>{" "}
                <span className="text-muted-foreground">(and What It Isn't)</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Most of the time, Pomodoro doesn't fail — it just gets misunderstood. 
                This is the clean reset: what Pomodoro actually is, how students should use it, 
                and why it's still one of the best "starter systems" for studying smarter.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              {/* Sidebar TOC - Desktop */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 max-w-3xl">
                {/* Table of Contents - Mobile */}
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                {/* Lead Paragraph */}
                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  If you've heard of the Pomodoro Technique, there's a good chance you've also heard at least one of these:
                </p>

                <div className="bg-muted/30 border border-border rounded-xl p-6 my-8">
                  <ul className="space-y-3 text-foreground/80 font-body italic">
                    <li>"It's just a timer."</li>
                    <li>"25 minutes isn't enough to get anything done."</li>
                    <li>"It's only for people with short attention spans."</li>
                    <li>"I tried it once and it didn't work."</li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  So this page is the clean reset: what Pomodoro actually is, how students should use it, what people mess up, and why it's still one of the best "starter systems" for studying smarter.
                </p>

                {/* What Pomodoro Actually Is */}
                <div id="what-is-pomodoro" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        What the Pomodoro Technique Actually Is
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  At its core, the Pomodoro Technique is a <strong>focus-and-break system</strong>. You work in short, timed intervals, take intentional breaks, and repeat.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-6">
                  The classic structure is:
                </p>

                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-8">
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><strong>25 minutes</strong> of focused work</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><strong>5 minutes</strong> of break</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Repeat <strong>4 times</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Then take a <strong>longer break</strong></span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  That's the popular version you'll see everywhere — and it's rooted in the original method created by{" "}
                  <a href="https://francescocirillo.com/products/the-pomodoro-technique" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Francesco Cirillo</a>.
                </p>

                <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 my-8">
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-0">
                    But here's the part people miss:<br /><br />
                    <strong className="text-xl">Pomodoro isn't about the timer.</strong><br />
                    <strong className="text-xl">It's about protecting focus for a short, defined window.</strong><br /><br />
                    The timer just creates a boundary your brain can respect.
                  </p>
                </div>

                {/* What Pomodoro Is NOT */}
                <div id="what-pomodoro-is-not" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-7 h-7 text-destructive" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        What Pomodoro Is NOT
                      </h2>
                    </div>
                  </div>
                </div>

                {/* Not about speed */}
                <div className="mb-10">
                  <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-destructive" />
                    Pomodoro is not about speed
                  </h3>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                    You're not racing the clock. You're building real focus — the kind that doesn't collapse after 15 minutes.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-foreground/90">
                    A "successful Pomodoro" isn't measured by how fast you went. It's measured by whether you stayed on one task without drifting.
                  </p>
                </div>

                {/* Not multitasking */}
                <div className="mb-10">
                  <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-destructive" />
                    Pomodoro is not multitasking with breaks
                  </h3>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                    If you spend your 25 minutes switching tabs, replying to messages, and half-studying… that's not Pomodoro.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                    This matters because{" "}
                    <a href="https://www.pnas.org/doi/10.1073/pnas.0903620106" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">research shows multitasking</a>{" "}
                    (and frequent task-switching) is linked to worse performance and weaker ability to filter distractions.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 font-medium">
                    Pomodoro works best when it's <strong>one task, one block, one focus target</strong>.
                  </p>
                </div>

                {/* Not rigid */}
                <div className="mb-10">
                  <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-destructive" />
                    Pomodoro is not rigid
                  </h3>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                    25 minutes is not sacred.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-foreground/90">
                    The point is the rhythm: <strong>focus → break → focus → break</strong>. You can adjust the timing later once you understand the method.
                  </p>
                </div>

                {/* Not a personality test */}
                <div className="mb-12">
                  <h3 className="text-xl font-display font-bold mb-4 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-destructive" />
                    Pomodoro is not a personality test
                  </h3>
                  <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                    You didn't "fail" Pomodoro if it felt hard.
                  </p>
                  <p className="text-lg leading-relaxed font-body text-foreground/90">
                    If your focus is messy at first, that's normal. Pomodoro is literally a <strong>training wheel for attention</strong>.
                  </p>
                </div>

                {/* Why 25 Minutes */}
                <div id="why-25-minutes" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why 25 Minutes Became the Default
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  25 minutes isn't magic. It's <strong>psychologically safe</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-4">
                  It's short enough that you can tell yourself:
                </p>

                <blockquote className="border-l-4 border-primary pl-6 my-8">
                  <p className="text-xl italic text-foreground/80 font-body">
                    "Okay, I can do just 25 minutes."
                  </p>
                </blockquote>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  And that matters because <strong>starting is the hardest part</strong> for most students.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  A 25-minute session feels like a small commitment — but it often creates momentum. That's one reason Pomodoro is such a popular student strategy in{" "}
                  <a href="https://en.wikipedia.org/wiki/Pomodoro_Technique" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">productivity education resources</a>{" "}
                  today.
                </p>

                {/* Breaks Are Fuel */}
                <div id="breaks-are-fuel" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Coffee className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Breaks Are Part of the System (Not a Reward)
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The biggest mistake students make is treating breaks like something you "earn."
                </p>

                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-8 text-center">
                  <p className="text-xl font-display font-bold text-foreground mb-2">
                    Breaks are not a reward.
                  </p>
                  <p className="text-xl font-display font-bold text-primary">
                    Breaks are fuel.
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  <a href="https://pubmed.ncbi.nlm.nih.gov/21211793/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Research supports</a>{" "}
                  the idea that brief, planned mental breaks can help prevent vigilance decline and keep performance steadier over time.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Even{" "}
                  <a href="https://health.cornell.edu/about/news/study-breaks-why-do-they-matter" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">university wellness resources</a>{" "}
                  consistently recommend purposeful breaks to improve energy and focus during studying.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12 font-medium">
                  So yes — if you're skipping breaks, Pomodoro will feel worse and stop working.
                </p>

                {/* How to Use Pomodoro */}
                <div id="how-to-use-pomodoro" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        How Students Should Actually Use Pomodoro (Simple Version)
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-6">
                  Here's the version that works for real life:
                </p>

                <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-xl p-8 my-8">
                  <ol className="space-y-4 text-foreground/90 font-body">
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">1</span>
                      <div>
                        <strong>Pick one specific task</strong>
                        <p className="text-muted-foreground mt-1">Not "study math" → "finish 5 calculus problems"</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">2</span>
                      <span><strong>Set a timer for 25 minutes</strong></span>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">3</span>
                      <span><strong>Study with no distractions</strong> until it ends</span>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">4</span>
                      <span><strong>Stop when the timer ends</strong></span>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">5</span>
                      <span><strong>Take a 5-minute break</strong></span>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">6</span>
                      <span><strong>Repeat</strong></span>
                    </li>
                  </ol>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The goal isn't perfection. The goal is <strong>showing up repeatedly</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-muted-foreground mb-12">
                  Try the <Link to="/study-timer/" className="text-primary hover:underline">CSW Study Timer</Link> to get started with built-in Pomodoro intervals.
                </p>

                {/* Pomodoro + Study Streams */}
                <div id="pomodoro-and-study-streams" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Pomodoro Fits So Well with Study Streams
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro gets stronger when the rhythm is shared.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-6">
                  <Link to="/study-stream/" className="text-primary hover:underline">Study streams</Link> and{" "}
                  <Link to="/study-room/" className="text-primary hover:underline">virtual study rooms</Link> add:
                </p>

                <div className="bg-muted/30 border border-border rounded-xl p-6 my-8">
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><strong>"Everyone is working right now"</strong> energy</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><strong>Synchronized start/stop times</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><strong>Accountability without pressure</strong></span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This lines up with broader research on co-working environments and focus: shared work settings can improve persistence and reduce drifting.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  That's why Pomodoro is basically the backbone of so many <Link to="/study-with-me/" className="text-primary hover:underline">online focus rooms</Link>.
                </p>

                {/* If Pomodoro Didn't Work Before */}
                <div id="if-pomodoro-didnt-work" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <RefreshCw className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        If Pomodoro Didn't Work for You Before…
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-6">
                  Usually one of these happened:
                </p>

                <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-6 my-8">
                  <ul className="space-y-3 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                      <span>You <strong>multitasked</strong> during focus blocks</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                      <span>You <strong>skipped breaks</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                      <span>You used <strong>vague goals</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                      <span>You expected <strong>instant results</strong></span>
                    </li>
                    <li className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-1" />
                      <span>You <strong>tried it once and quit</strong></span>
                    </li>
                  </ul>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  <strong>Pomodoro isn't a hack. It's a system.</strong><br />
                  And systems work through repetition.
                </p>

                {/* Final Thought */}
                <div id="final-thought" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Final Thought
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  <strong>Pomodoro works because it respects attention.</strong>
                </p>

                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-8 my-8 text-center">
                  <p className="text-xl font-display font-bold text-foreground leading-relaxed">
                    Short focus.<br />
                    Clear boundaries.<br />
                    Planned breaks.<br />
                    <span className="text-primary">Repeat.</span>
                  </p>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Once you understand what it is (and what it isn't), it becomes one of the easiest productivity habits to stick to — especially when paired with{" "}
                  <Link to="/study-stream/" className="text-primary hover:underline">study streaming</Link> or{" "}
                  <Link to="/study-room/" className="text-primary hover:underline">shared study rooms</Link>.
                </p>

                {/* Sources */}
                <div id="sources" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Sources
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 border border-border rounded-xl p-6 my-8">
                  <ul className="space-y-4 text-foreground/90 font-body">
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://francescocirillo.com/products/the-pomodoro-technique" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Official Pomodoro Technique site (Cirillo)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://francescocirillo.com/pages/pomodoro-technique" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Cirillo PDF (method overview + classic 25/5 structure)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://en.wikipedia.org/wiki/Pomodoro_Technique" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Wikipedia overview (history + standard steps)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://www.pnas.org/doi/10.1073/pnas.0903620106" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Multitasking research (PNAS: cognitive control in media multitaskers)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://pubmed.ncbi.nlm.nih.gov/19619181/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Multitasking research (PubMed entry for same study)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://pubmed.ncbi.nlm.nih.gov/21211793/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Breaks improve sustained attention (Ariga & Lleras, Cognition – PubMed)
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://news.illinois.edu/view/6367/205427" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        University of Illinois news release summarizing the break research
                      </a>
                    </li>
                    <li className="flex items-start gap-3">
                      <ExternalLink className="w-4 h-4 text-primary flex-shrink-0 mt-1.5" />
                      <a href="https://health.cornell.edu/about/news/study-breaks-why-do-they-matter" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Cornell Health: study breaks improve focus and productivity
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Back to Hub */}
                <div className="mt-12 mb-16">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Pomodoro Technique Hub
                  </Link>
                </div>

                {/* Related Articles */}
                <section className="mt-16 py-12 px-6 bg-gradient-to-br from-primary/5 via-background to-secondary/5 rounded-2xl border border-border">
                  <div className="flex items-center gap-3 mb-8">
                    <BookOpen className="w-6 h-6 text-primary" />
                    <h2 className="text-2xl font-display font-bold">Related Articles</h2>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group block p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all duration-300"
                      >
                        <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {article.description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default Pomodoro101;
